import React, { useState } from 'react';

import { BaseFiltersPanel } from '../../Paylinks/components/BaseFiltersPanel';
import {
  PortalUserAuditLogFiltersFormValues,
  portalUserAuditLogFiltersEmptyValues,
  usePortalUserAuditLogFiltersForm,
} from '../../../hooks/usePortalUserAuditLogFiltersForm';
import type {
  GetPortalUserAuditLogsQuery,
  PortalUserAuditEmailType,
  PortalUserAuditLogAction,
  PortalUserAuditLogFilterOption,
} from '../../../store/modules/merchant/types';
import { PortalUserAuditLogFilters } from './PortalUserAuditLogFilters';

export type PortalUserAuditLogQueryFilters = Pick<
  GetPortalUserAuditLogsQuery,
  'action' | 'emailType' | 'createdAfter' | 'createdBefore'
>;

const EMAIL_FILTER_OPTIONS = new Set<PortalUserAuditLogFilterOption>([
  'activation',
  'passwordReset',
]);

export const convertToQueryFilters = (
  data: PortalUserAuditLogFiltersFormValues,
): PortalUserAuditLogQueryFilters => {
  const filters: PortalUserAuditLogQueryFilters = {};

  const coreActions = data.actions.filter(
    (option): option is Extract<PortalUserAuditLogFilterOption, 'create' | 'update' | 'delete'> =>
      !EMAIL_FILTER_OPTIONS.has(option),
  );
  const emailTypes = data.actions.filter((option): option is PortalUserAuditEmailType =>
    EMAIL_FILTER_OPTIONS.has(option),
  );

  const actions: PortalUserAuditLogAction[] = [...coreActions];
  if (emailTypes.length > 0) {
    actions.push('sendAccessEmail');
  }

  if (actions.length > 0) filters.action = actions;
  if (emailTypes.length > 0) filters.emailType = emailTypes;
  if (data.createdAfter?.isValid()) filters.createdAfter = data.createdAfter.toISOString();
  if (data.createdBefore?.isValid()) filters.createdBefore = data.createdBefore.toISOString();

  return filters;
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onFiltersApply: (filters: PortalUserAuditLogQueryFilters) => void;
  initialFilterState?: Partial<PortalUserAuditLogFiltersFormValues>;
}

export const PortalUserAuditLogFiltersPanel = ({
  isOpen,
  onClose,
  onFiltersApply,
  initialFilterState = {},
}: Props) => {
  const [lastApplied, setLastApplied] = useState({
    ...portalUserAuditLogFiltersEmptyValues,
    ...initialFilterState,
  });

  const {
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
    handleSubmit,
  } = usePortalUserAuditLogFiltersForm({
    initialValues: initialFilterState,
  });

  const onSubmit = (data: PortalUserAuditLogFiltersFormValues) => {
    onFiltersApply(convertToQueryFilters(data));
    setLastApplied(data);
    onClose();
  };

  const onCancel = () => {
    reset(lastApplied);
    onClose();
  };

  return (
    <BaseFiltersPanel
      isOpen={isOpen}
      onCancel={onCancel}
      onReset={() => reset(portalUserAuditLogFiltersEmptyValues)}
      onSubmit={handleSubmit(onSubmit)}
      filtersCount={(() => {
        const applied = convertToQueryFilters(watch());
        let count = 0;
        if (applied.action?.length || applied.emailType?.length) count += 1;
        if (applied.createdAfter) count += 1;
        if (applied.createdBefore) count += 1;
        return count;
      })()}
    >
      <PortalUserAuditLogFilters
        hookFormProps={{
          watch,
          setValue,
          control,
          errors,
        }}
      />
    </BaseFiltersPanel>
  );
};
